import { AmChart, AmChartsService } from '@amcharts/amcharts3-angular';
import { Directive, Injectable, OnDestroy } from '@angular/core';
import { Axis, ChartRelativeInput, LinesChartInput } from 'emma-common-ts/emma';
import { omit } from 'lodash';

import { formatNumber } from '@emma-helpers/format.helper';
import { isCurrencyCode } from '@platform/helpers/currency.helper';
import { EMMAChartBase } from './emma-chart-base';
import { getOptions, lineStyles } from './emma-lineschart.component.helpers';

@Injectable({
  providedIn: 'root',
})
@Directive()
// tslint:disable-next-line: directive-class-suffix
export class EMMAChartAms extends EMMAChartBase implements OnDestroy {
  protected override chart: AmChart | undefined;
  protected data: any;
  protected parseDates = true;

  constructor(protected AmCharts: AmChartsService) {
    super();
    this.container = 'div';
  }

  protected getOptions(): any {
    return getOptions();
  }

  protected getLineStyles(): any {
    return lineStyles;
  }

  override init(): void {
    if (!window.document.getElementById(this.chartId)) {
      throw new Error('Chart not found ' + this.chartId);
    }
    this.chart = this.AmCharts.makeChart(this.chartId, {
      ...this.getOptions(),
      export: { enabled: Boolean(this.export), menu: [] },
    });
    this.chartLoad.emit(true);
    if (this.export) {
      this.export.subscribe(() => this.exportChart());
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.AmCharts.destroyChart(this.chart);
    }
  }

  protected generateAmChartAxis = ({ axis, label, options }: Axis) => {
    return {
      ...options,
      id: `ValueAxis-${axis}`,
      axisAlpha: 0,
      gridAlpha: 0.08,
      precision: 0,
      title: label,
      position: axis % 2 ? 'left' : 'right',
    };
  };

  protected _generateAxis = ({ axis, label = '', options = {} }: Axis) => {
    return this.generateAmChartAxis({ axis, label, options });
  };

  protected appendChartGraph = (graph: any, chartGraphs: any[]): void => {
    if (graph) {
      const style: any = { ...this.getLineStyles()[graph.index] };
      if (graph.lineOptions) {
        if (graph.lineOptions.fill) {
          style.fillColors = style.lineColor;
          style.fillAlphas = 1;
        }
        Object.assign(style, omit(graph.lineOptions, ['fill']));
      }
      const axis = typeof graph.axis === 'object' ? graph.axis.axis : graph.axis || 1;
      chartGraphs.push({
        ...style,
        title: graph.title,
        valueAxis: `ValueAxis-${axis}`,
        valueField: graph.label,
        balloonText: `[[value]]`,
        balloonFunction: this.balloonFunction,
      });
    }
  };

  balloonFunction(item: any, graph: any): string {
    if (isCurrencyCode(item.graph.valueField)) {
      return graph.balloonText.replace(
        '[[value]]',
        formatNumber(item.dataContext[item.graph.valueField], {
          style: 'currency',
          currency: item.graph.valueField,
        })
      );
    }
    const balloonText = `${graph.balloonText} ${item.graph.valueField}`;
    return balloonText.replace('[[value]]', formatNumber(item.dataContext[item.graph.valueField]));
  }

  addAxis(axis: Axis): void {
    this.data.axes[axis.axis - 1] = { ...axis };
  }

  removeAxis(axisNumber: number) {
    this.data.axes[axisNumber - 1] = undefined;
    this.updateChart();
  }

  reset(): void {
    this.data = { labels: [], graphs: [], axes: [] };
    this.updateChart();
  }

  override loading(data?: any): void {
    if (!data) {
      this.reset();
    }
    super.loading();
  }

  override loaded(data?: any): void {
    if (!data || !this.data.graphs.find((g: { loading: boolean }) => Boolean(g && g.loading === true))) {
      super.loaded();
    }
  }

  override error(error?: Error): void {
    this.reset();
    super.error(error);
  }

  isRelative(data: LinesChartInput | ChartRelativeInput): data is ChartRelativeInput {
    return Object.prototype.hasOwnProperty.call(data as ChartRelativeInput, 'total');
  }
}
